<script setup lang="ts">
import type { Iti } from 'intl-tel-input';
import { computed, onBeforeUnmount, onMounted, ref, useId, watch } from 'vue';
import { classNames } from '@/js/lib/bem';
import {
  createPhoneInput,
  PHONE_INVALID_MESSAGE,
  phoneE164,
  phoneValidationMessage,
} from '@/js/lib/phone-input';

const props = withDefaults(
  defineProps<{
    label: string
    name: string
    modelValue?: string
    autocomplete?: string
    disabled?: boolean
    invalid?: boolean
    message?: string
    required?: boolean
  }>(),
  {
    modelValue: '',
    autocomplete: 'tel',
    disabled: false,
    invalid: false,
    message: undefined,
  },
);

const emit = defineEmits<{
  'update:modelValue': [value: string]
}>();

const inputId = useId();

const cn = classNames('base-field');

const inputRef = ref<HTMLInputElement>();
const invalidNumber = ref(false);
const touched = ref(false);

let iti: Iti | null = null;

const showInvalid = computed(() => touched.value && invalidNumber.value);

const fieldClasses = computed(() =>
  cn(props.invalid || showInvalid.value ? { state: 'invalid' } : undefined),
);

const shownMessage = computed(() =>
  props.message ?? (showInvalid.value ? PHONE_INVALID_MESSAGE : undefined),
);

// The model always carries E.164; the input itself shows the national format.
function sync(): void {
  const input = inputRef.value;

  if (iti === null || input === undefined)
    return;

  const value = input.value.trim();
  const validationMessage = phoneValidationMessage(iti, value);

  invalidNumber.value = validationMessage !== '';
  input.setCustomValidity(validationMessage);
  emit('update:modelValue', value === '' ? '' : phoneE164(iti));
}

function onInput(): void {
  touched.value = false;
  sync();
}

function onBlur(): void {
  touched.value = true;
}

onMounted(() => {
  if (inputRef.value === undefined)
    return;

  iti = createPhoneInput(inputRef.value);

  if (props.modelValue !== '')
    iti.setNumber(props.modelValue);

  void iti.promise.then(sync);
});

onBeforeUnmount(() => {
  iti?.destroy();
  iti = null;
});

watch(
  () => props.modelValue,
  (value) => {
    // Only react to changes coming from outside, not to what `sync` just emitted.
    if (iti === null || value === phoneE164(iti))
      return;

    iti.setNumber(value);
  },
);
</script>

<template>
  <div :class="[fieldClasses, cn('stacked')]">
    <div :class="cn('control')">
      <input
        :id="inputId"
        ref="inputRef"
        :name="name"
        type="tel"
        inputmode="tel"
        :autocomplete="autocomplete"
        :disabled="disabled"
        :required="required"
        placeholder=" "
        @input="onInput"
        @countrychange="sync"
        @blur="onBlur"
      >
      <label :for="inputId" class="base-field__label">{{ label }}</label>
    </div>
    <p v-if="shownMessage" :class="cn('message')" role="alert">
      {{ shownMessage }}
    </p>
  </div>
</template>

<style lang="scss" scoped>
.base-field {
  &__stacked {
    flex-direction: column;
  }

  &__control {
    position: relative;
  }

  &__message {
    margin: 4px 0 0 6px;
    color: var(--base-field-font-color);
    font-size: 12px;
    line-height: 1.3;
  }
}
</style>
